import {Injectable} from '@angular/core';

export interface TimeSlot {
    openTime: string;
    closeTime: string;
}

export interface DayOpeningHours {
    day: string;
    dayShort: string;
    isOpen: boolean;
    timeSlots: TimeSlot[];
}

export type OpeningHoursData = DayOpeningHours[];

export const DAYS_OF_WEEK = [
    { day: "Monday", dayShort: "Mon" },
    { day: "Tuesday", dayShort: "Tue" },
    { day: "Wednesday", dayShort: "Wed" },
    { day: "Thursday", dayShort: "Thu" },
    { day: "Friday", dayShort: "Fri" },
    { day: "Saturday", dayShort: "Sat" },
    { day: "Sunday", dayShort: "Sun" },
];

@Injectable({
    providedIn: 'root'
})
export class OpeningHoursService {

    // Constants for 24-hour logic, used by both Parser and UI Component
    public readonly TIME_24H_START = '12:00 AM';
    public readonly TIME_24H_END = '11:59 PM';

    constructor() { }

    getDefaultOpeningHours(): OpeningHoursData {
        return DAYS_OF_WEEK.map(d => ({
            ...d,
            isOpen: false,
            timeSlots: [],
        }));
    }

    /**
     * Generates the HTML Unordered List (<ul>) for the opening hours.
     * Output format is strictly AM/PM.
     * Does NOT include an <h3> title.
     */
    generateOpeningHoursHtmlList(data: OpeningHoursData): string {
        let html = '<ul>\n';

        data.forEach(day => {
            html += '  <li>';
            html += `<strong>${day.dayShort}:</strong> `;

            if (day.isOpen && day.timeSlots.length > 0) {
                const slotStrings = day.timeSlots.map(slot => {
                    const startKey = this.formatToAmPm(slot.openTime);
                    const endKey = this.formatToAmPm(slot.closeTime);

                    // Check for 24 Hours coverage
                    if (day.timeSlots.length === 1 &&
                        (startKey === this.TIME_24H_START && (endKey === this.TIME_24H_END || endKey === "12:00 AM"))) {
                        return '24 Hours';
                    }

                    return `${startKey} - ${endKey}`;
                });
                html += slotStrings.join(', ');
            } else if (day.isOpen) {
                // Fallback if marked open but no slots defined
                html += 'Open';
            } else {
                html += 'Closed';
            }
            html += '</li>\n';
        });

        html += '</ul>';
        return html;
    }

    /**
     * Parses HTML string into OpeningHoursData object.
     * Handles inputs with or without <h3> headers.
     * Converts 24h strings (14:00) to AM/PM (02:00 PM) automatically.
     */
    parseOpeningHoursHtml(htmlString: string | null | undefined): OpeningHoursData {
        const defaultHours = this.getDefaultOpeningHours();

        // Safety check for SSR or empty input
        if (typeof window === 'undefined' || !htmlString) {
            return defaultHours;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        // We only look for LI elements.
        // This makes the parser title-agnostic (works with or without <h3>).
        const listItems = doc.querySelectorAll('li');

        const parsedDaysData: { [key: string]: Partial<Omit<DayOpeningHours, 'day' | 'dayShort'>> } = {};

        listItems.forEach(li => {
            const strongTag = li.querySelector('strong');
            if (!strongTag?.textContent) return;

            const dayNameFromHtml = strongTag.textContent.replace(':', '').trim();
            const dayConfig = this.findDayConfig(dayNameFromHtml);
            if (!dayConfig) return;

            const dayKey = dayConfig.day;

            // Extract text content representing the hours
            let hoursText = '';

            // Strategy: Try to get the text node immediately following the <strong> tag
            if (strongTag.nextSibling && strongTag.nextSibling.nodeType === Node.TEXT_NODE) {
                hoursText = strongTag.nextSibling.textContent || '';
            }
            // Fallback: Get full text and remove the day name part
            else {
                const fullText = li.textContent || '';
                // e.g. "Mon: 9:00 - 5:00" remove "Mon:"
                hoursText = fullText.substring(fullText.indexOf(':') + 1);
            }

            hoursText = hoursText.trim();
            const newTimeSlots: TimeSlot[] = [];

            // Case 1: Closed
            if (hoursText.toLowerCase().includes('closed')) {
                parsedDaysData[dayKey] = { isOpen: false, timeSlots: [] };
            }
            // Case 2: 24 Hours text
            else if (hoursText.toLowerCase().includes('24 hours')) {
                newTimeSlots.push({ openTime: this.TIME_24H_START, closeTime: this.TIME_24H_END });
                parsedDaysData[dayKey] = { isOpen: true, timeSlots: newTimeSlots };
            }
            // Case 3: "Open" (Generic)
            else if (hoursText.toLowerCase() === 'open') {
                parsedDaysData[dayKey] = { isOpen: true, timeSlots: [] };
            }
            // Case 4: Specific Time Slots
            else {
                const slotStrings = hoursText.split(',').map(s => s.trim());
                for (const slotStr of slotStrings) {
                    if (!slotStr) continue;
                    const timeParts = slotStr.split('-').map(t => t.trim());

                    if (timeParts.length === 2) {
                        // formatToAmPm handles both 14:00 and 2:00 PM inputs
                        const openTime = this.formatToAmPm(timeParts[0]);
                        const closeTime = this.formatToAmPm(timeParts[1]);

                        if (openTime && closeTime) {
                            newTimeSlots.push({ openTime, closeTime });
                        }
                    }
                }

                if (newTimeSlots.length > 0) {
                    parsedDaysData[dayKey] = { isOpen: true, timeSlots: newTimeSlots };
                } else {
                    // If parsing failed or empty, default to closed
                    parsedDaysData[dayKey] = { isOpen: false, timeSlots: [] };
                }
            }
        });

        // Merge parsed data into the default structure to ensure all 7 days exist
        return defaultHours.map(defaultDay => {
            const parsedDayInfo = parsedDaysData[defaultDay.day];
            return parsedDayInfo ? { ...defaultDay, ...parsedDayInfo } : defaultDay;
        });
    }

    private findDayConfig(dayString: string): { day: string; dayShort: string } | undefined {
        if (!dayString) return undefined;
        const normalizedDay = dayString.trim().toLowerCase();
        return DAYS_OF_WEEK.find(
            d => d.day.toLowerCase() === normalizedDay || d.dayShort.toLowerCase() === normalizedDay
        );
    }

    /**
     * Converts any time string to standardized "HH:MM AA" format.
     *
     * Examples:
     * "14:30" -> "02:30 PM"
     * "9:00" -> "09:00 AM"
     * "5:00 pm" -> "05:00 PM"
     */
    private formatToAmPm(timeStrInput: string): string | null {
        if (!timeStrInput) return null;
        // Clean up input (remove dots like a.m., trim spaces)
        let timeStr = timeStrInput.trim().toLowerCase().replace(/\./g, '');

        // Check for 24-Hour Format (e.g., 14:00, 09:00)
        const twentyFourHourMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
        if (twentyFourHourMatch) {
            let hours = parseInt(twentyFourHourMatch[1], 10);
            const minutes = twentyFourHourMatch[2];
            const ampm = hours >= 12 ? 'PM' : 'AM';

            hours = hours % 12;
            hours = hours ? hours : 12; // Convert '0' hours to '12'

            return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        }

        // Check for Existing AM/PM Format (e.g., 5:00 pm, 05:00pm)
        const amPmMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
        if (amPmMatch) {
            let hours = parseInt(amPmMatch[1], 10);
            const minutes = amPmMatch[2];
            const ampm = amPmMatch[3].toUpperCase();

            // Just ensure padding for single digit hours (e.g. 9:00 -> 09:00)
            return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        }

        return null;
    }
}
